import {
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import { useState, useEffect } from 'react';
import { FaEdit } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { PiWarningCircleBold } from 'react-icons/pi';
import * as React from 'react';
import { IoMdAdd } from 'react-icons/io';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Form } from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import ReactPaginate from 'react-paginate';
import { IoSearchOutline } from 'react-icons/io5';
import { Formik, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { getAdmins, addSubAdmin, updateSubAdmin, deleteSubAdmin } from '../../../redux/actions/SuperAdmin.action';
import { getPlans } from '../../../redux/actions/Plan.action';
import logo from '../../../assets/img/zokep-edit.png';

const SubAdminList = () => {
  const dispatch = useDispatch();
  const admins = useSelector((state) => state.superAdmin);

  const plans = useSelector((state) => state.plan);

  const [show, setShow] = useState(false);
  const [updateModal, setUpdateModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('');
  const [allowPlanUpdate, setAllowPlanUpdate] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const indexOfLastRow = currentPage * rowsPerPage;
  const totalCount = admins?.getAdmins?.data?.totalCount || 0;

  const handleAddModalClose = () => setShow(false);
  const handleAddModalOpen = () => setShow(true);

  const handleUpdateModalClose = () => {
    setUpdateModal(false);
    setSelectedAdmin(null);
    setAllowPlanUpdate(false);
  };

  const handleUpdateModalOpen = (admin) => {
    setSelectedAdmin(admin);
    setUpdateModal(true);
  };

  const handleDeleteModalClose = () => {
    setDeleteModal(false);
    setSelectedAdmin(null);
  };

  const handleDeleteModalOpen = (admin) => {
    setSelectedAdmin(admin);
    setDeleteModal(true);
  };

  const initialValues = {
    name: '',
    email: '',
    phoneNumber: '',
    type: 'Admin',
    planId: '',
    startDate: '',
    endDate: '',
    password: '',
    facebookPageAccessToken: '',
    logo: '' // Add logo field
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Name is required'),
    email: Yup.string().email('Invalid email').required('Email is required'),
    phoneNumber: Yup.string().required('Phone number is required'),
    planId: Yup.string().when('$isUpdate', {
      is: true,
      then: (schema) => allowPlanUpdate ? schema.required('Plan is required') : schema,
      otherwise: (schema) => schema.required('Plan is required')
    }),
    startDate: Yup.date().when('$isUpdate', {
      is: true,
      then: (schema) => allowPlanUpdate ? schema.required('Start date is required') : schema,
      otherwise: (schema) => schema.required('Start date is required')
    }),
    endDate: Yup.date().when('$isUpdate', {
      is: true,
      then: (schema) => allowPlanUpdate ? schema.required('End date is required') : schema,
      otherwise: (schema) => schema.required('End date is required')
    }),
    password: Yup.string().required('Password is required'),
    facebookPageAccessToken: Yup.string()
  });

  useEffect(() => {
    dispatch(getPlans({
      page: 1,
      limit: 100
    }));
  }, [dispatch]);

  useEffect(() => {
    const params = {
      page: currentPage,
      limit: rowsPerPage,
      search: searchQuery,
      planId: selectedPlan
    };
    dispatch(getAdmins(params));
  }, [dispatch, currentPage, rowsPerPage, searchQuery, selectedPlan]);

  const handleSubmit = async (values, { resetForm }) => {
    setIsSubmitting(true); // Set loading state to true
    const formData = new FormData();
    
    Object.keys(values).forEach(key => {
      if (key === 'logo' && values[key]) {
        formData.append('logo', values[key]);
      } else {
        formData.append(key, values[key]);
      }
    });

    try {
      await dispatch(addSubAdmin(formData, {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery
      }));
      resetForm();
      handleAddModalClose();
    } finally {
      setIsSubmitting(false); // Set loading state to false regardless of success/failure
    }
  };

  const handleUpdate = (values) => {
    const formData = new FormData();

    // Append all form fields to FormData
    Object.keys(values).forEach(key => {
      if (key === 'logo') {
        if (values[key]) {
          formData.append('logo', values[key]);
        }
      } else {
        if (values[key] !== undefined && values[key] !== null) {
          formData.append(key, values[key]);
        }
      }
    });

    dispatch(updateSubAdmin(selectedAdmin._id, formData, {
      page: currentPage,
      limit: rowsPerPage,
      search: searchQuery
    })).then(() => {
      handleUpdateModalClose();
    });
  };

  const handleDelete = () => {
    if (selectedAdmin) {
      dispatch(deleteSubAdmin(selectedAdmin._id, {
        page: currentPage,
        limit: rowsPerPage,
        search: searchQuery
      })).then(() => {
        handleDeleteModalClose();
      });
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (selected) => {
    setCurrentPage(selected.selected + 1);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setCurrentPage(1);
  };

  const formatDateForUI = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const renderFormFields = ({ handleSubmit, setFieldValue, values, handleClose }) => (
    <Form onSubmit={handleSubmit} encType="multipart/form-data">
      <Modal.Body>
        <div className="row">
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Name</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={values.name}
                onChange={(e) => setFieldValue('name', e.target.value)}
                placeholder="Enter name"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="name" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control
                type="email"
                name="email"
                value={values.email}
                onChange={(e) => setFieldValue('email', e.target.value)}
                placeholder="Enter email"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="email" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Phone Number</Form.Label>
              <Form.Control
                type="text"
                name="phoneNumber"
                value={values.phoneNumber}
                onChange={(e) => setFieldValue('phoneNumber', e.target.value)}
                placeholder="Enter phone number"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="phoneNumber" component="div" className="text-danger" />
            </Form.Group>
          </div>

          {selectedAdmin && (
            <>
              <div className="col-12 mb-3">
                <Form.Check
                  type="switch"
                  id="active-status-switch"
                  label="Active Status"
                  checked={values.isActive}
                  onChange={(e) => setFieldValue('isActive', e.target.checked)}
                  className="custom-switch"
                />
              </div>
              <div className="col-12 mb-3">
                <Form.Check
                  type="switch"
                  id="plan-update-switch"
                  label="Enable Plan Update"
                  checked={allowPlanUpdate}
                  onChange={(e) => setAllowPlanUpdate(e.target.checked)}
                  className="custom-switch"
                />
              </div>
            </>
          )}

          {(!selectedAdmin || allowPlanUpdate) && (
            <>
              <div className="col-12">
                <Form.Group className="mb-3">
                  <Form.Label>Plan</Form.Label>
                  <Form.Select
                    name="planId"
                    value={values.planId}
                    onChange={(e) => {
                      const selectedPlan = plans?.getPlans?.data?.data?.find(plan => plan._id === e.target.value);

                      const today = new Date();
                      const startDate = today.toISOString().split('T')[0];
                      let endDate = '';

                      if (selectedPlan) {
                        const endDateObj = new Date();
                        endDateObj.setMonth(endDateObj.getMonth() + selectedPlan.duration);
                        endDate = endDateObj.toISOString().split('T')[0];
                      }

                      setFieldValue('planId', e.target.value);
                      setFieldValue('startDate', startDate);
                      setFieldValue('endDate', endDate);
                    }}
                    className="shadow-none focus:!border-theme-dark"
                  >
                    <option value="">Select Plan</option>
                    {plans?.getPlans?.data?.data && plans.getPlans.data.data.map((plan) => (
                      <option key={plan._id} value={plan._id}>
                        {plan.planName} ({plan.duration} {plan.duration === 1 ? 'Month' : 'Months'})
                      </option>
                    ))}
                  </Form.Select>
                  <ErrorMessage name="planId" component="div" className="text-danger" />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="text"
                    name="startDate"
                    value={values.startDate ? new Date(values.startDate).toLocaleDateString('en-GB') : ''}
                    readOnly
                    className="shadow-none focus:!border-theme-dark"
                  />
                  <input
                    type="hidden"
                    name="startDate"
                    value={values.startDate}
                  />
                  <ErrorMessage name="startDate" component="div" className="text-danger" />
                </Form.Group>
              </div>
              <div className="col-6">
                <Form.Group className="mb-3">
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="text"
                    name="endDate"
                    value={values.endDate ? new Date(values.endDate).toLocaleDateString('en-GB') : ''}
                    readOnly
                    className="shadow-none focus:!border-theme-dark"
                  />
                  <input
                    type="hidden"
                    name="endDate"
                    value={values.endDate}
                  />
                  <ErrorMessage name="endDate" component="div" className="text-danger" />
                </Form.Group>
              </div>
            </>
          )}

          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Password</Form.Label>
              <Form.Control
                type="text"
                name="password"
                value={values.password}
                onChange={(e) => setFieldValue('password', e.target.value)}
                placeholder="Enter password"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="password" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Facebook Page Access Token</Form.Label>
              <Form.Control
                type="text"
                name="facebookPageAccessToken"
                value={values.facebookPageAccessToken}
                onChange={(e) => setFieldValue('facebookPageAccessToken', e.target.value)}
                placeholder="Enter Facebook Page Access Token"
                className="shadow-none focus:!border-theme-dark"
              />
              <ErrorMessage name="facebookPageAccessToken" component="div" className="text-danger" />
            </Form.Group>
          </div>
          <div className="col-12">
            <Form.Group className="mb-3">
              <Form.Label>Logo</Form.Label>
              <Form.Control
                type="file"
                name="logo"
                accept="image/*"
                onChange={(e) => {
                  setFieldValue('logo', e.currentTarget.files[0]);
                }}
                className="shadow-none focus:!border-theme-dark"
              />
              {selectedAdmin && selectedAdmin.logo && (
                <div className="mt-2">
                  <img
                    src={selectedAdmin.logo}
                    alt="Current Logo"
                    style={{ maxWidth: '100px' }}
                    className="mt-2"
                  />
                </div>
              )}
            </Form.Group>
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="secondary"
          onClick={handleClose}
          className="px-4 bg-transparent border-[1px] text-black border-green-color"
          disabled={isSubmitting}
        >
          Close
        </Button>
        <Button
          type="submit"
          className="!bg-theme-dark border-0 px-4 d-flex align-items-center gap-2"
          disabled={isSubmitting}
        >
          {selectedAdmin ? 'Update' : 'Add'}
        </Button>
      </Modal.Footer>
    </Form>
  );

  return (
    <>     
       {isSubmitting && (
        <div className="loaderAdd">
          <div className="loader_line"><img src={logo} className="w-[200px]" /></div>
        </div>
      )}
      <div className="mt-[100px] d-flex justify-content-between">
        <div className="d-flex items-center gap-3">
          <Form.Group>
            <Form.Select
              value={selectedPlan}
              onChange={(e) => setSelectedPlan(e.target.value)}
              className="shadow-none focus:!border-theme-dark"
            >
              <option value="">All Plans</option>
              {plans?.getPlans?.data?.data?.map((plan) => (
                <option key={plan._id} value={plan._id}>
                  {plan.planName}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </div>
        <div className="d-flex items-center gap-3">
          <div className="bg-white shadow-shadows py-[8px] px-[20px] rounded-full flex items-center gap-2">
            <IoSearchOutline />
            <input
              type="text"
              value={searchQuery}
              onChange={handleSearch}
              className="w-full outline-none"
              placeholder="Search..."
            />
          </div>
          <Button
            onClick={handleAddModalOpen}
            className="!bg-theme-dark fw-bold d-flex align-item-center border-0"
          >
            <IoMdAdd className="mt-1 gap-1" />
            Add Admin
          </Button>
        </div>
      </div>

      <div className="mt-[50px] mb-4">
        <div className='mb-3 flex items-center justify-between mt-5'>
          <div className='d-flex align-items-center gap-2'>
            <h3 className='fw-bold mb-0'>All Admins List</h3>
          </div>
          <div className='d-flex align-items-center gap-4'>
            <span className='bg-gray-100 text-gray-800 px-3 py-1 rounded-full text-sm fw-bold'>
              Total : <span className="text-theme-dark">{totalCount}</span>
            </span>
            <div className='text-gray-800 fw-bold'>
              Rows per page:
            </div>
            <select
              value={rowsPerPage}
              onChange={handleRowsPerPageChange}
              className="px-3 py-1 border rounded-md focus:outline-none focus:border-theme-dark fw-bold text-gray-800"
            >
              <option value={10}>10</option>
              <option value={30}>30</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        <TableContainer>
          <Table variant="striped" colorScheme="blackAlpha">
            <Thead>
              <Tr>
                <Th>Logo</Th>
                <Th>Name</Th>
                <Th>Email</Th>
                <Th>Phone</Th>
                <Th>Plan</Th>
                <Th>Status</Th>
                <Th>Create By</Th>
                <Th>Branches</Th>
                <Th>Managers</Th>
                <Th>Leads</Th>
                <Th className="text-end">Action</Th>
              </Tr>
            </Thead>
            <Tbody>
              {admins?.getAdmins?.data?.AuthData && admins?.getAdmins?.data?.AuthData.length > 0 ? (
                admins?.getAdmins?.data?.AuthData.map((admin) => (
                  <Tr key={admin._id}>
                    <Td>
                      {admin.logo ? (
                        <img 
                          src={admin.logo} 
                          alt={`${admin.name}'s logo`} 
                          style={{ width: '50px', height: '50px', objectFit: 'contain' }}
                        />
                      ) : (
                        'No Logo'
                      )}
                    </Td>
                    <Td>{admin.name}</Td>
                    <Td>{admin.email}</Td>
                    <Td>{admin.phoneNumber}</Td>
                    <Td>{admin.planId?.planName || 'No Plan'}</Td>
                    <Td>
                      <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${admin.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {admin.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </Td>
                    <Td>{admin.admin_created_by?.name || ''}</Td>
                    <Td>{admin.branchCount || 0}</Td>
                    <Td>{admin.managerCount || 0}</Td>
                    <Td>{admin.leadCount || 0}</Td>
                    <Td>
                      <div className="d-flex gap-3 justify-end">
                        <FaEdit
                          onClick={() => handleUpdateModalOpen(admin)}
                          className="cursor-pointer border-green-color fs-5 text-success"
                          title="update"
                        />
                        <MdDelete
                          onClick={() => handleDeleteModalOpen(admin)}
                          className="cursor-pointer border-green-color fs-5 text-danger"
                          title="delete"
                        />
                      </div>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={9} textAlign='center'>No Sub Admins Found</Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </TableContainer>
      </div>

      <ReactPaginate
        breakLabel="..."
        nextLabel="next >"
        onPageChange={handlePageChange}
        pageRangeDisplayed={5}
        pageCount={Math.ceil(totalCount / rowsPerPage)}
        previousLabel="< previous"
        renderOnZeroPageCount={null}
        className="pagination"
      />

      {/* Add Modal */}
      <Modal show={show} onHide={handleAddModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Add Sub Admin</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {(formikProps) => renderFormFields({
            ...formikProps,
            handleClose: handleAddModalClose
          })}
        </Formik>
      </Modal>

      {/* Update Modal */}
      <Modal show={updateModal} onHide={handleUpdateModalClose}>
        <Modal.Header closeButton>
          <Modal.Title>Update Sub Admin</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={{
            name: selectedAdmin?.name || '',
            email: selectedAdmin?.email || '',
            phoneNumber: selectedAdmin?.phoneNumber || '',
            type: selectedAdmin?.type || 'Admin',
            planId: selectedAdmin?.planId?._id || '',
            startDate: selectedAdmin?.startDate?.split('T')[0] || '',
            endDate: selectedAdmin?.endDate?.split('T')[0] || '',
            password: selectedAdmin?.plainPassword || '',
            facebookPageAccessToken: selectedAdmin?.facebookPageAccessToken || '',
            isActive: selectedAdmin?.isActive || false
          }}
          validationSchema={validationSchema}
          onSubmit={handleUpdate}
          enableReinitialize
          context={{ isUpdate: true }}
        >
          {(formikProps) => renderFormFields({
            ...formikProps,
            handleClose: handleUpdateModalClose
          })}
        </Formik>
      </Modal>

      {/* Delete Modal */}
      <Modal show={deleteModal} onHide={handleDeleteModalClose}>
        <Modal.Body className='p-5'>
          <div>
            <PiWarningCircleBold className='text-7xl w-100 text-danger' />
          </div>
          <div className='text-center mt-4'>
            <h3>Are You Sure?</h3>
            <p className='text-md'>Do you really want to delete this sub admin?</p>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={handleDeleteModalClose}
            className="px-4 bg-transparent border-[1px] text-black border-green-color"
          >
            No
          </Button>
          <Button
            type="button"
            className="bg-danger border-0 px-4"
            onClick={handleDelete}
          >
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default SubAdminList;